import React, { useEffect, useState } from 'react';
import {
    Card,
    Box,
    CardContent,
    Typography,
    FormControl,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material';
import TotalFunds from '../charts/TotalFund';
import { CCarousel, CCarouselItem } from '@coreui/react';
import { getUserFromLocalStorage } from '../../../utils/utils';
import Axios from '../../../config/axios';
import { UrlConstant } from '../../../commom/UrlConstant';
import DonutChart from '../charts/DonutChart';
import LinechartMiniPortfolio from '../charts/LinechartMiniPortfolio';
import { usePriceFormatter } from '../../../hooks';
import { formatPercentage } from '../../../utils/formatting';
import { useAppSelector } from '../../../Redux/hooks';

function Yourinvestment({ onboardType }) {

    const user = getUserFromLocalStorage();
    const formatter = usePriceFormatter()
    const [selectedTypeStock, setSelectedTypeStock] = useState<any>('stockChart');
    const [selectedTypeETF, setSelectedTypeETF] = useState<any>('etfChart');
    const [loading, setLoading] = useState<any>(false);
    const [tradingInfo, setTradingInfo] = useState<any>();
    const [portfolioData, setPortfolioData] = useState<any>()
    const [investmentChart, setInvestmentChart] = useState<any>()

    const [stockChart, setStockChart] = useState<any>()
    const [stockChartLabel, setStockLabel] = useState<any>()

    const [ETFChart, setETFChart] = useState<any>()
    const [ETFChartLabel, setETFLabel] = useState<any>()

    const [cryptoChart, setCryptoChart] = useState<any>()
    const [cryptoChartLabel, setCryptoLabel] = useState<any>()

    const [investmentData, setInvestmentData] = useState<any>()
    const [investmentFilter, setInvestmentFilter] = useState<any>("1W")
    const [changeInvestmentData, setChangeInvestmentData] = useState<any>()
    const [changePercentageInvestmentData, setChangePercentageInvestmentData] = useState<any>()
    const [accountValue, setAccountValue] = useState<any>()

    const currencyInfo = useAppSelector((state) => state.currency);

    const CRYPTO_CHART_LIMIT = 5;

    const handleChange = (event: SelectChangeEvent, chart) => {
        if (chart === "Stocks") {
            setSelectedTypeStock(event.target.value as string);
        } else if (chart === "ETFs") {
            setSelectedTypeETF(event.target.value as string);
        } else if (chart === "Investment") {
            setInvestmentFilter(event.target.value as string)
        }
    };

    const convertToPercentage = (data: number[]) => {
        const total = data.reduce((acc, value) => acc + value, 0);
        if (total === 0) return data.map(() => 0);

        const rawPercentages = data.map((value) => (value / total) * 100);
        // const adjustedPercentages = rawPercentages.map((percentage) => (percentage > 0 && percentage < 1 ? 1 : Math.trunc(percentage)));

        return rawPercentages;
    };

    const fetchStockInvestmentChart = async () => {
        setLoading(true);
        try {
            const response = await Axios.get(
                UrlConstant.InvestmentChart.getInvestmentChart.replace("{0}", investmentFilter)
            );

            if (response.data?.status) {
                let lastValue: number | null = null;

                const chartData = response.data?.data?.responseData?.map((item) => {
                    let value = item.accountValue;

                    // If value is missing or zero on a weekend -> fallback to previous day's value
                    if (!value && lastValue !== null) {
                        value = lastValue;
                    }

                    lastValue = value; // update last known good value

                    return {
                        x: new Date(item.recordDate),
                        y: value
                    };
                });

                setInvestmentData(chartData);
                setChangeInvestmentData(response.data?.data?.change);
                setChangePercentageInvestmentData(response.data?.data?.changePercentage);
                setAccountValue(response.data?.data?.accountValue);
            }
        } catch (error) {
            console.error("ERRR : ", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCryptoInvestmentChart = async () => {
        setLoading(true);
        try {
            const cryptoRes = await Axios.get(
                `${UrlConstant.Crypto.GetCryptoInvestmentChart}?filter=${investmentFilter}`
            );

            if (
                cryptoRes.data?.status &&
                Array.isArray(cryptoRes.data.data?.responseData)
            ) {
                const chartData = cryptoRes.data.data.responseData
                    .map((item: any) => ({
                        x: new Date(item.x),
                        y: item.y,
                    }))
                    .sort((a: any, b: any) => +a.x - +b.x);

                setInvestmentData(chartData);
                const cryptoChange = cryptoRes.data.data?.change;
                const cryptoChangePercentage = cryptoRes.data.data?.changePercentage;
                const cryptoAccountValue = cryptoRes.data.data?.accountValue;
                const firstValue = chartData[0]?.y ?? 0;
                const lastValue = chartData[chartData.length - 1]?.y ?? 0;

                setChangeInvestmentData(
                    typeof cryptoChange === 'number'
                        ? cryptoChange
                        : chartData.length > 0
                            ? lastValue - firstValue
                            : 0
                );
                setChangePercentageInvestmentData(
                    typeof cryptoChangePercentage === 'string'
                        ? cryptoChangePercentage
                        : firstValue > 0
                            ? (((lastValue - firstValue) / firstValue) * 100).toFixed(2)
                            : '0.00'
                );
                setAccountValue(
                    typeof cryptoAccountValue === 'number'
                        ? cryptoAccountValue
                        : lastValue
                );
            } else {
                setInvestmentData([]);
                setChangeInvestmentData(0);
                setChangePercentageInvestmentData("0.00");
                setAccountValue(0);
            }
        } catch (error: any) {
            console.error("ERRR : ", error);
            setInvestmentData([]);
            setChangeInvestmentData(0);
            setChangePercentageInvestmentData("0.00");
            setAccountValue(0);
        } finally {
            setLoading(false);
        }
    };

    const fetchTradingBalance = async () => {
        setLoading(true);
        try {
            if (onboardType === 'crypto') {
                const currency = currencyInfo.code?.toLowerCase() || 'usd';
                const response = await Axios.get(UrlConstant.User.GetUserBalance, {
                    params: { type: 'fiat', currency }
                });
                const balanceList = Array.isArray(response.data.data.balances) ? response.data.data.balances : [];
                const fiatObj = balanceList.find(item => item.currency === currency);

                const tradingData = {
                    Balances: {
                        CashBalance: Number(fiatObj?.available) ?? 0,
                    },
                    BuyingPower: {
                        AvailableFunds: Number(fiatObj?.available) ?? 0,
                    },
                };

                setTradingInfo(tradingData);
            } else {
                const data = { accountId: user?.tradingblock_application_id };
                const response = await Axios.post(UrlConstant.BuySell.GetTradingBalance, data);
                setTradingInfo(response.data?.data?.Payload);
            }
        } catch (error: any) {
            console.error("ERRR : ", error);
        } finally {
            setLoading(false);
        }
    };

    // Enhanced fetchPortfolioData with crypto support
    const fetchPortfolioData = async () => {
        setLoading(true)
        try {
            if (onboardType === "crypto") {
                // Fetch only crypto portfolio
                const cryptoRes = await fetchCryptoPortfolio();
                const cryptoList = cryptoRes.portfolio || [];
                const totalCryptoValue = cryptoList.reduce((sum, item) => sum + (item.currentValue || 0), 0);
                const portfolioDataWithCrypto = {
                    cryptoList: cryptoList || [],
                    cryptoChart: {
                        cryptoTotal: totalCryptoValue || 0,
                        chartDataTest: (cryptoList || []).map(crypto => ({
                            symbol: crypto.code || crypto.symbol,
                            amount: crypto.totalValue || crypto.currentValue || 0,
                            percentage: crypto.percentage || 0
                        })),
                    },
                };
                setPortfolioData(portfolioDataWithCrypto);
            } else if (onboardType === "stock") {
                // Fetch only stock/ETF portfolio
                const stockRes = await (async () => {
                    const data = { accountId: user?.tradingblock_application_id }
                    const res = await Axios.post(UrlConstant.Portfolio.GetPortfolio, data)
                    return res?.data?.data || {}
                })();
                setPortfolioData(stockRes);
            }
        } catch (error: any) {
            console.error("ERRR : ", error);
        } finally {
            setLoading(false)
        }
    }

    const fetchCryptoPortfolio = async () => {
        try {
            const response = await Axios.get(UrlConstant.User.Portfolio)
            return response?.data?.data || { portfolio: [], summary: {} }
        } catch (error: any) {
            console.error("Crypto portfolio error : ", error)
            return { portfolio: [], summary: {} }
        }
    }

    // Updated totalcards array with conditional rendering based on onboardType
    const getTotalCards = () => {
        const baseCards: any = [];

        // Investment Portfolio card - show for both types but with different data
        baseCards.push({
            heading: "Investment Portfolio",
            data: investmentChart,
            label: onboardType === "crypto" ? ["Crypto", "Cash"] : ["Stocks", "Cash", "ETFs"],
            chartType: "Donut"
        });

        // Investments chart card - show for both types
        baseCards.push({
            heading: "Investments",
            data: investmentData,
            tabOptions: [{ label: "1W", value: "1W" }, { label: "1M", value: "1M" }, { label: "3M", value: "3M" }, { label: "6M", value: "6M" }, { label: "1Y", value: "1Y" }, { label: "5Y", value: "5Y" }],
            selectedOption: investmentFilter,
            label: onboardType === "crypto" ? ["Crypto"] : ["Stocks", "ETFs"],
            chartType: "Line",
            subtitle: true,
        });

        if (onboardType === "stock") {
            baseCards.push({
                heading: "Stocks",
                Selectoptions: [
                    { label: "Assets", value: "stockChart" },
                    { label: "Sector", value: "sectorChart" }
                ],
                selectedOption: selectedTypeStock,
                data: stockChart || [],
                label: stockChartLabel,
                chartType: "Donut",
            });
            baseCards.push({
                heading: "ETFs",
                Selectoptions: [
                    { label: "Assets", value: "etfChart" },
                    { label: "Category", value: "categoryChart" }
                ],
                selectedOption: selectedTypeETF,
                data: ETFChart || [],
                label: ETFChartLabel,
                chartType: "Donut",
            });
        } else if (onboardType === "crypto") {
            baseCards.push({
                heading: "Assets",
                Selectoptions: [{ label: "Assets", value: "cryptoChart" }],
                selectedOption: "cryptoChart",
                data: cryptoChart || [],
                label: cryptoChartLabel || [],
                chartType: "Donut"
            });
        }

        return baseCards;
    };

    const totalcards = getTotalCards();

    useEffect(() => {
        if (currencyInfo.loading) return;
        fetchTradingBalance();
        fetchPortfolioData();
    }, [onboardType, currencyInfo.loading]);

    useEffect(() => {
        if (onboardType === "stock") {
            fetchStockInvestmentChart();
        } else if (onboardType === "crypto") {
            fetchCryptoInvestmentChart();
        }
    }, [investmentFilter, onboardType]);

    // Enhanced investment chart calculation based on onboardType
    useEffect(() => {
        const cash = tradingInfo?.Balances?.CashBalance || 0

        if (onboardType === "crypto") {
            const crypto = portfolioData?.cryptoChart?.cryptoTotal || 0
            const data = [crypto, cash]
            const labels = ["Crypto", "Cash"]

            const finalDataPer = data?.length && convertToPercentage(data)
            const finalData = labels?.map((item, i) => {
                return {
                    label: item,
                    value: data[i],
                    percentage: finalDataPer[i]
                }
            })
            setInvestmentChart(finalData)
        } else if (onboardType === "stock") {
            const stock = portfolioData?.stockChart?.stockTotal || 0
            const ETF = portfolioData?.mainChart?.chartData?.ETF || 0
            const data = [stock, ETF, cash]
            const labels = ["Stocks", "ETFs", "Cash"]

            const finalDataPer = data?.length && convertToPercentage(data)
            const finalData = labels?.map((item, i) => {
                return {
                    label: item,
                    value: data[i],
                    percentage: finalDataPer[i]
                }
            })
            setInvestmentChart(finalData)
        }
    }, [tradingInfo, portfolioData, onboardType])

    useEffect(() => {
        if (onboardType === "stock") {
            let data = portfolioData?.[selectedTypeStock]?.chartDataTest
            const finalData = data?.map((item) => item.amount)
            const finalDataLabel = data?.map((item) => item.symbol)
            const finalDataPer = finalData?.length && convertToPercentage(finalData)
            const newData = finalDataLabel?.map((item, i) => {
                return {
                    label: item,
                    value: finalData[i],
                    percentage: finalDataPer[i]
                }
            })
            console.log('Stock Chart Data:', newData)
            setStockChart(newData)
            setStockLabel(finalDataLabel)
        }
    }, [portfolioData, selectedTypeStock, onboardType])

    useEffect(() => {
        if (onboardType === "stock") {
            let data = portfolioData?.[selectedTypeETF]?.chartDataTest
            const finalData = data?.map((item) => item.amount)
            const finalDataLabel = data?.map((item) => item.symbol)
            const finalDataPer = finalData?.length && convertToPercentage(finalData)
            const newData = finalDataLabel?.map((item, i) => {
                return {
                    label: item,
                    value: finalData[i],
                    percentage: finalDataPer[i]
                }
            })
            setETFChart(newData)
            setETFLabel(finalDataLabel)
        }
    }, [portfolioData, selectedTypeETF, onboardType])

    // Crypto chart effect - only for crypto onboardType
    useEffect(() => {
        if (onboardType === "crypto") {
            const cryptoData = portfolioData?.cryptoList || [];
            console.log('Crypto Data for Chart:', cryptoData);

            if (cryptoData.length > 0) {
                const formattedData = cryptoData.map(item => ({
                    label: item.code || item.symbol,
                    value: item.totalValue || item.currentValue || 0
                }));

                const sortedData = formattedData.sort((a, b) => b.value - a.value);

                let limitedData = [...sortedData];
                if (sortedData.length > CRYPTO_CHART_LIMIT) {
                    const topCoins = sortedData.slice(0, CRYPTO_CHART_LIMIT - 1);
                    const otherCoins = sortedData.slice(CRYPTO_CHART_LIMIT - 1);
                    const otherValue = otherCoins.reduce((sum, coin) => sum + coin.value, 0);

                    limitedData = [...topCoins];
                    if (otherValue > 0) {
                        limitedData.push({ label: 'Other', value: otherValue });
                    }
                }

                const valueArray = limitedData.map(item => item.value);
                const percentages = valueArray.length ? convertToPercentage(valueArray) : [];

                const chartData = limitedData.map((item, index) => ({
                    label: item.label,
                    value: item.value,
                    percentage: percentages[index]
                }));

                console.log('Processed Crypto Chart Data:', chartData);
                setCryptoChart(chartData);
                setCryptoLabel(limitedData.map(item => item.label));
            } else {
                setCryptoChart([]);
                setCryptoLabel([]);
            }
        }
    }, [portfolioData, onboardType])

    // Helper function to render bottom section based on onboardType
    const renderBottomSection = () => {
        if (onboardType === "crypto") {
            return (
                <Box display="flex" width="100%" justifyContent="space-between" position='absolute' bottom='0px' left='0px'>
                    <Box p={3} width="50%" borderRight="1px solid #666666" borderTop="1px solid #666666" justifyContent="center">
                        <Typography className="text-secondary" textAlign="center" gutterBottom>Cash</Typography>
                        <Typography fontSize="18px" className="text-white" fontWeight='bold' textAlign="center">
                            {formatter.balance(tradingInfo?.Balances?.CashBalance).display}
                        </Typography>
                    </Box>
                    <Box p={3} width="50%" borderTop="1px solid #666666" justifyContent="center">
                        <Typography className="text-secondary" textAlign="center" gutterBottom>Crypto</Typography>
                        <Typography fontSize="18px" className="text-white" fontWeight='bold' textAlign="center">{formatter.balance(portfolioData?.cryptoChart?.cryptoTotal).display}</Typography>
                    </Box>
                </Box>
            );
        } else if (onboardType === "stock") {
            return (
                <Box display="flex" width="100%" justifyContent="space-between" position='absolute' bottom='0px' left='0px'>
                    <Box p={3} width="34%" borderRight="1px solid #666666" borderTop="1px solid #666666" justifyContent="center">
                        <Typography className="text-secondary" textAlign="center" gutterBottom>Cash</Typography>
                        <Typography fontSize="18px" className="text-white" fontWeight='bold' textAlign="center">
                            {tradingInfo?.Balances?.CashBalance >= 0
                                ? `${formatter.balance(tradingInfo?.Balances?.CashBalance).display}`
                                : `-${formatter.balance(tradingInfo?.Balances?.CashBalance).display}`}
                        </Typography>
                    </Box>
                    <Box p={3} width="34%" borderRight="1px solid #666666" borderTop="1px solid #666666" justifyContent="center">
                        <Typography className="text-secondary" textAlign="center" gutterBottom>Stocks</Typography>
                        <Typography fontSize="18px" className="text-white" fontWeight='bold' textAlign="center">{formatter.balance(portfolioData?.stockChart?.stockTotal).display}</Typography>
                    </Box>
                    <Box p={3} width="32%" borderTop="1px solid #666666" justifyContent="center">
                        <Typography className="text-secondary" textAlign="center" gutterBottom>ETFs</Typography>
                        <Typography fontSize="18px" className="text-white" fontWeight='bold' textAlign="center">{formatter.balance(portfolioData?.mainChart?.chartData?.ETF).display}</Typography>
                    </Box>
                </Box>
            );
        }
    };

    return (
        <Card className="card" sx={{ width: '100%', height: '100%', paddingBottom: '0px' }}>
            <CardContent sx={{ paddingBottom: '0px', padding: '30px' }}>
                <CCarousel controls indicators interval={false}>
                    {totalcards.map((card, index) => (
                        <CCarouselItem key={index} style={{ color: 'white', padding: '0px 10px' }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    width="100%"
                                >
                                    <Box flexShrink={0}>
                                        <Typography className="text-white" variant="h5" noWrap>
                                            {card.heading}
                                        </Typography>
                                        {card.chartType === "Line" && (
                                            <Typography variant="subtitle1" className="text-white" style={{ textAlign: "left" }}>
                                                {formatter.balance(accountValue).display}
                                            </Typography>
                                        )}
                                    </Box>

                                    {card.Selectoptions && (
                                        <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0 }}>
                                            <Select
                                                value={card.selectedOption}
                                                className="text-white"
                                                sx={{
                                                    borderRadius: "9px !important",
                                                    ".MuiSvgIcon-root": { color: "white !important" },
                                                    fontSize: "14px",
                                                }}
                                                onChange={(e) => { handleChange(e, card.heading) }}
                                            >
                                                {card.Selectoptions.map((option, idx) => (
                                                    <MenuItem key={idx} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                    {card.subtitle &&

                                        <Box display="flex" flexDirection={"column"} alignItems="flex-end" justifyContent="end" >
                                            <span style={{ color: changeInvestmentData >= 0 ? "#4edf6d" : "#ff2a2a" }}>{` ${formatter.display(changeInvestmentData, { showSign: true })} (${formatPercentage(changePercentageInvestmentData, { showSign: true })})`}</span>
                                            <Typography className="text-secondary" fontSize="14px" fontWeight="bold" textAlign="center">Past {card.selectedOption}</Typography>
                                        </Box>
                                    }
                                </Box>

                            </Box>
                            {card.chartType === "Line" ? (
                                <Box>
                                    <LinechartMiniPortfolio height={"275px"} data={card?.data || []} lineColor={
                                        changeInvestmentData > 0 ? "#4edf6d" : changeInvestmentData === 0 ? "#4edf6d" : "#ff2a2a"
                                    } />
                                    <Box display="flex" justifyContent="center" alignItems="center">
                                        {card?.tabOptions?.map((item, index) => (
                                            <Box
                                                key={index}
                                                p={"3px 12px"}
                                                m={1} style={{ cursor: 'pointer' }}
                                                onClick={() => setInvestmentFilter(item.value)}
                                                sx={{
                                                    backgroundColor: investmentFilter === item.value ? "#F2674A" : 'transparent',
                                                    color: 'white',
                                                    border: investmentFilter === item.value ? "" : '1px solid #FFFFFF80',
                                                    borderRadius: '50px',
                                                    fontSize: '14px'
                                                }}
                                            >
                                                {item.label}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ) : (
                                <DonutChart
                                    series={card?.data?.length ? card?.data : []}
                                    labels={card?.label?.length ? card?.label : []}
                                />
                            )}
                        </CCarouselItem>
                    ))}
                </CCarousel>

                {/* Bottom Section: Conditionally rendered based on onboardType */}
                {renderBottomSection()}
            </CardContent>
        </Card>
    );
}

export default Yourinvestment;